import { Theme as MuiTheme } from '@material-ui/core';
import { Breakpoints } from '@material-ui/core/styles/createBreakpoints';
import { Palette, PaletteColor, TypeText } from '@material-ui/core/styles/createPalette';
import { Color } from '@material-ui/lab/Alert';
import {
  BorrowerOnboardingDetails,
  BusinessOnboardingDetails,
  CreditCardBorrower,
  CreditCardDetailsResponse,
  MinimalOnboardingDetails,
  OnboardingDetailsRole,
  OnlineOnboardingDetails,
  Role,
  UserType,
} from '@nemuru-eng/js-api-client';
import { ReactNode } from 'react';

import { DYNAMIC_PAGE_PATHS } from '../components/views/DynamicPageView/config/shared/pathRoutes';
import { APP_ROLES } from './config';

export type TextId = string;
export type PathId = string;
export type IconName = string;

export interface Link {
  path: PathId;
  icon: IconName;
}

export interface NavSection {
  nameTextId?: TextId;
  links: Link[];
}

export interface Widget {
  id: JSX.Element;
  roles: AppRole[];
  notShownInRightMenu?: boolean;
  props: any;
}

export interface PermissionsScheme {
  view: UserType[];
  edit?: Role[];
}

export interface RoleBasedScheme {
  roles: AppRole[];
}

export interface UserTypeBasedScheme {
  userTypes: UserType[];
}

export interface ModuleCorePermissionProps {
  canEdit: boolean;
}

export interface SectionScheme {
  textId: any;
  module: ReactNode;
}

export interface SchemeByRole extends RoleBasedScheme {
  titleTextId: string;
  widgets: Widget[];
}

export type AppRole = (typeof APP_ROLES)[keyof typeof APP_ROLES];

export interface ModuleScheme extends RoleBasedScheme {
  modules: ReactNode[];
}

type dynamicPagePathsKeys = keyof typeof DYNAMIC_PAGE_PATHS;

export interface PageScheme {
  titleTextId?: TextId;
  path: (typeof DYNAMIC_PAGE_PATHS)[dynamicPagePathsKeys];
  schemesByRoles: SchemeByRole[];
  widgets?: Widget[];
  sections: SectionScheme[];
  showIfAllowedProduct?: ApplicationType;
}

export interface DynamicPage {
  titleTextId?: TextId;
  subtitleTextId?: TextId;
  widgets?: Widget[];
  sections: SectionScheme[];
}

export interface Layout {
  navSections: NavSection[];
  pagesByPath: {
    [key: string]: DynamicPage;
  };
  showSuperSearchBar: boolean;
}

export enum ApplicationType {
  CONSUMER_LOAN = 'consumer_loan',
  AUTO = 'auto',
  CREDIT_LINE = 'credit_line',
  CARD = 'card',
  ORDER = 'order',
  PAY_BY_LINK = 'pay_by_link',
  CHECKOUT_SESSION = 'checkout_session',
  A2A_PAYMENT = 'a2a_payment',
  CARD_PAYMENT = 'card_payment',
}

export type SinglePaymentApplicationType = ApplicationType.CARD_PAYMENT | ApplicationType.A2A_PAYMENT;

export enum PermissionsConditionType {
  AND,
  OR,
}

export interface SnackbarNotification {
  msg?: JSX.Element | string;
  level: Color;
  error?: any;
}

export interface AlertNotification {
  tId?: string;
  retryFn?: () => void;
}

//Material UI Theme Interfaces

export interface CustomTypeText extends TypeText {
  title?: string;
}

export interface CustomPalette extends Palette {
  type: 'light' | 'dark';
  text: CustomTypeText;
  minor: PaletteColor;
  input: PaletteColor;
}

export interface Theme extends Omit<MuiTheme, 'palette' | 'shadows'> {
  zendeskCustomerSupport?: boolean;
  themeName: string;
  logoUrl?: string;
  background?: string;
  palette: CustomPalette;
  breakpoints: Breakpoints;
  customBorderRadius: {
    button: number;
    paper: number;
    chip: number;
  };
  shadows: string[];
  assetPackDefsId?: 'filled' | 'outlined';
}

export enum PendingDataType {
  BORROWER_ONBOARDING = 'borrower_onboarding',
  BORROWER_DOCUMENTATION = 'borrower_documentation',
  BORROWER_FINANCIAL_INFORMATION = 'borrower_financial_information',
  DEFAULT = 'default',
  ADDRESS = 'address',
  CREDENTIALS = 'credentials',
  AGENT_PRICINGS = 'agent_pricings',
  PAYMENT_PLAN = 'payment_plan',
  PAYMENT_ATTEMPTS = 'payment_attempts',
  KYC_VERIFICATION = 'kyc_verification',
  RISK_ANALYSIS = 'risk_analysis',
  CART = 'cart',
  ACTIVITY = 'activity',
  CUSTOMER_SUPPORT = 'customer_support',
  NO_CHANNELS_SELECTED = 'no_channels_selected',
  NO_METRICS_INFORMATION = 'no_metrics_information',
  NO_RISK_INFORMATION = 'no_risk_information',
  NO_REASONS_GIVEN = 'no_reasons_given',
  PASSWORD_RECOVERY_NOT_FOUND = 'password_recovery_not_found',
  COMPLIANCE = 'compliance',
  PAYOUT_DETAILS = 'payout_details',
  NEW_APPLICATION = 'new_application',
  NO_DATA_TO_DISPLAY = 'new_application_cart',
  NO_PAYMENT_INTENTS = 'no_payment_intents',
  NO_WEBHOOK_CONFIGURED = 'NO_WEBHOOK_CONFIGURED',
  NO_FUNNEL_DATA = 'no_funnel_data',
  NO_PRICINGS_AVAILABLE = 'no_pricings_available',
  NO_AGENTS_IN_CHANNEL = 'no_agents_in_channel',
  NO_NOTIFICATIONS = 'no_notifications',
  NO_DOCUMENTATION_FOUND = 'no_documentation_found',
  NO_LOAN_FOUND = 'no_loan_found',
  CANCELLED_CARD = 'cancelled_card',
  NO_LENDER_CONFIGURATION_FOUND = 'no_lender_configuration_found',
}

export interface Account {
  name?: string;
  info?: string | JSX.Element;
}

export type ComponentConfig = LoanListByProductConfig; // in the future we can add different conponent config types

export interface ComponentCommonConfig {
  id: string;
}

export interface LoanListByProductConfig extends ComponentCommonConfig {
  query: any;
  filterDateValues?: { fromDate?: Date; toDate?: Date };
  activeBooleanFilterIds?: string[];
}

export enum ParticipantRole {
  BORROWER = 'BORROWER',
  CO_BORROWER = 'CO_BORROWER',
  GUARANTOR = 'GUARANTOR',
  AGENT = 'AGENT',
  CHANNEL = 'CHANNEL',
  MERCHANT = 'MERCHANT',
}

export enum NemuruBorrowerParticipantRoleQueryParam {
  borrower = 'b',
  co_borrower = 'c',
  guarantor = 'g',
}

export enum StatusTagType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning',
  DEFAULT = 'default',
  MINOR = 'minor',
}

export interface StatusDefinition {
  DESCRIPTION_T_ID: string;
  colorPaletteName: StatusTagType;
  colorType: 'light' | 'dark' | 'main';
  SECTION_T_ID: string;
}

export interface Participant {
  name: string;
  role: ParticipantRole;
  id: string;
  onClick?: () => void;
  linkTo?: string;
}

export type CreditCard = CreditCardDetailsResponse;
export type CreditCardConfigurationType = CreditCard['configuration'];
export type CreditCardContract = CreditCard['contract'];
export type CreditCardOnboardingDetails = CreditCardBorrower['onboardingDetails'];
export type CreditCardWorkflow = CreditCardBorrower['workflow'];

export type OnboardingDetails =
  | BorrowerOnboardingDetails
  | OnlineOnboardingDetails
  | MinimalOnboardingDetails
  | BusinessOnboardingDetails
  | CreditCardBorrowerOnboardingDetails;

export interface CreditCardBorrowerOnboardingDetails extends CreditCardOnboardingDetails {
  role: OnboardingDetailsRole;
}
export interface ApplicationParticipant {
  role: OnboardingDetailsRole;
  onboardingDetails?: OnboardingDetails;
}

export interface PageHighlightType {
  icon: string;
}

export enum PageHighlightTypeName {
  INCIDENCE = 'incidence',
  ERROR = 'error',
}

export const PageHighlightTypes: Record<PageHighlightTypeName, PageHighlightType> = {
  [PageHighlightTypeName.INCIDENCE]: { icon: 'feedback' },
  [PageHighlightTypeName.ERROR]: { icon: 'report_problem' },
};

export interface PageHighlight {
  textId: TextId;
  type: PageHighlightTypeName;
  hashId: string;
}

export interface TableColumn {
  name: string;
  label: string;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  colWidth?: string;
  sticky?: boolean;
}
